// src/components/MobileBottomNav.js
import React from 'react';
import { Paper, BottomNavigation, BottomNavigationAction } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home as HomeIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  Settings as SettingsIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';

const navItems = (orgId) => [
  { label: 'Home', icon: <HomeIcon />, to: `/${orgId}/` },
  { label: 'Products', icon: <CategoryIcon />, to: `/${orgId}/products` },
  { label: 'Inventory', icon: <InventoryIcon />, to: `/${orgId}/inventory` },
  { label: 'Orders', icon: <ShoppingCartIcon />, to: `/${orgId}/orders` },
  { label: 'Customers', icon: <PeopleIcon />, to: `/${orgId}/customers` },
  { label: 'Suppliers', icon: <LocalShippingIcon />, to: `/${orgId}/suppliers` },
  { label: 'Reports', icon: <BarChartIcon />, to: `/${orgId}/reports` },
  { label: 'Settings', icon: <SettingsIcon />, to: `/${orgId}/settings` },
];

const MobileBottomNav = ({ orgId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const items = navItems(orgId);
  const navRef = React.useRef(null);

  // Robust match: treat `/org` and `/org/` as same; match startsWith for non-root
  const normalize = (p) => (p.endsWith('/') ? p.slice(0, -1) : p);
  const locPath = normalize(location.pathname);
  const currentIndex = Math.max(
    0,
    items.findIndex((i) => {
      const target = normalize(i.to);
      return target === `/${orgId}` ? locPath === target : locPath.startsWith(target);
    })
  );

  const [value, setValue] = React.useState(currentIndex);
  React.useEffect(() => setValue(currentIndex), [currentIndex]);

  // Keep the active tab (e.g., Home) in view on small screens
  React.useEffect(() => {
    const root = navRef.current;
    if (!root) return;
    const selected = root.querySelector('.Mui-selected');
    if (selected && typeof selected.scrollIntoView === 'function') {
      selected.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [value]);

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
    >
      <BottomNavigation
        ref={navRef}
        showLabels
        value={value}
        onChange={(_, newValue) => {
          setValue(newValue);
          navigate(items[newValue].to);
        }}
        sx={{
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          pl: 1, // ensure first item (Home) isn't cropped at the edge
          pr: 1,
          scrollPaddingLeft: 8,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 72,
            px: 1,
            flex: '0 0 auto',
          },
        }}
      >
        {items.map((item) => (
          <BottomNavigationAction key={item.to} label={item.label} icon={item.icon} />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
